import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Layout from "./components/Layout";
import HomePage from "./pages/HomePage";
import FdmPage from "./pages/FdmPage";
import ApplyPage from "./pages/ApplyPage";
import ContactPage from "./pages/ContactPage";
import PrivacyPage from "./pages/PrivacyPage";
import EtlbPage from "./pages/EtlbPage";
import InterepOverviewPage from "./pages/InterepOverviewPage";
import AviofpPage from "./pages/AviofpPage";
import ScreenshotsPage from "./pages/ScreenshotsPage";
import ScreenshotsAdminPage from "./pages/ScreenshotsAdminPage";
import FleetAdminPage from "./pages/FleetAdminPage";
import PartnersAdminPage from "./pages/PartnersAdminPage";
import FleetPage from "./pages/FleetPage";
import useFleetState from "./hooks/useFleetState";
import IntelexHomePage from "./pages/IntelexHomePage";
import IntelexNewReportPage from "./pages/IntelexNewReportPage";
import IntelexReportsPage from "./pages/IntelexReportsPage";
import IntelexReportDetailPage from "./pages/IntelexReportDetailPage";
import IntelexTemplateBuilderPage from "./pages/IntelexTemplateBuilderPage";
import IntelexReviewQueuePage from "./pages/IntelexReviewQueuePage";
import IntelexExamPage from "./pages/IntelexExamPage";
import IntelexExamAdminPage from "./pages/IntelexExamAdminPage";
import InterepAdminSettingsPage from "./pages/InterepAdminSettingsPage";
import InterepTestLoginPage from "./pages/InterepTestLoginPage";
import { ConfirmationDialogHost } from "./intelex/ConfirmationDialog";

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

function DeploymentUpdateGuard() {
  const { pathname } = useLocation();

  useEffect(() => {
    let stopped = false;
    const check = async () => {
      try {
        const response = await fetch(`/asset-manifest.json?check=${Date.now()}`, {
          cache: "no-store",
          headers: { Accept: "application/json" },
        });
        if (!response.ok || stopped) return;
        const manifest = await response.json();
        const latestMain = manifest?.files?.["main.js"];
        const loadedMain = Array.from(document.scripts)
          .map((script) => script.getAttribute("src") || "")
          .find((source) => /\/static\/js\/main\.[a-f0-9]+\.js(?:$|\?)/.test(source));
        if (
          latestMain &&
          loadedMain &&
          new URL(latestMain, window.location.origin).pathname !==
            new URL(loadedMain, window.location.origin).pathname
        ) {
          window.location.reload();
        }
      } catch (_error) {
        // A failed version check must never interrupt normal navigation.
      }
    };
    const onVisible = () => {
      if (!document.hidden) check();
    };
    check();
    window.addEventListener("pageshow", check);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      window.removeEventListener("pageshow", check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [pathname]);

  return null;
}

// Whether /fleet exists is set from the admin page, not in the code. Until the answer is in,
// nothing is drawn: redirecting first and correcting afterwards would bounce anyone opening
// the page directly.
function FleetRoute() {
  const { enabled } = useFleetState();
  if (enabled === null) return null;
  return enabled ? <FleetPage /> : <Navigate to="/" replace />;
}

function LegacyInterepRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={`${location.pathname.replace(/^\/intelex/, "/interep")}${location.search}${location.hash}`}
      replace
    />
  );
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <DeploymentUpdateGuard />
      <ConfirmationDialogHost />
      <Routes>
        <Route path="/interep" element={<IntelexHomePage />} />
        <Route path="/interep/test" element={<InterepTestLoginPage />} />
        <Route
          path="/interep/report/new/:templateId"
          element={<IntelexNewReportPage />}
        />
        <Route path="/interep/reports" element={<IntelexReportsPage />} />
        <Route
          path="/interep/reports/pirep/:pirepId"
          element={<IntelexReportDetailPage />}
        />
        <Route
          path="/interep/reports/reference/:eventNumber"
          element={<IntelexReportDetailPage />}
        />
        <Route
          path="/interep/reports/:reportId"
          element={<IntelexReportDetailPage />}
        />
        <Route
          path="/interep/admin/templates"
          element={<IntelexTemplateBuilderPage />}
        />
        <Route
          path="/interep/admin/reviews"
          element={<IntelexReviewQueuePage />}
        />
        <Route
          path="/interep/admin/responses"
          element={<IntelexReviewQueuePage mode="responses" />}
        />
        <Route path="/interep/exam" element={<IntelexExamPage />} />
        <Route path="/interep/admin/exams" element={<IntelexExamAdminPage />} />
        <Route
          path="/interep/admin/settings"
          element={<InterepAdminSettingsPage />}
        />
        <Route path="/intelex/*" element={<LegacyInterepRedirect />} />
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          {/* Fleet gallery is temporarily disabled until its livery images are restored. */}
          <Route path="/fleet" element={<FleetRoute />} />
          <Route
            path="/fleet.html"
            element={<Navigate to="/" replace />}
          />
          <Route path="/fdm" element={<FdmPage />} />
          <Route path="/fdm.html" element={<Navigate to="/fdm" replace />} />
          <Route path="/etlb" element={<EtlbPage />} />
          <Route path="/etlb.html" element={<Navigate to="/etlb" replace />} />
          <Route path="/interep-overview" element={<InterepOverviewPage />} />
          <Route
            path="/interep-overview.html"
            element={<Navigate to="/interep-overview" replace />}
          />
          {/* Not /aviofp: that path is the electronic flight bag itself, served by nginx
              from its own build, so the public page sits beside it under its own route. */}
          <Route path="/aviofp-overview" element={<AviofpPage />} />
          <Route
            path="/aviofp-overview.html"
            element={<Navigate to="/aviofp-overview" replace />}
          />
          <Route path="/screenshots" element={<ScreenshotsPage />} />
          <Route
            path="/screenshots.html"
            element={<Navigate to="/screenshots" replace />}
          />
          {/* Flat, not /screenshots/admin: the host resolves these routes by file and the
              build writes one .html per route (scripts/create-route-entrypoints.js), so a
              nested path would need its own nginx rule. Gated by the portal session and the
              admin role on the server; this route only decides which page is drawn. */}
          <Route path="/screenshots-admin" element={<ScreenshotsAdminPage />} />
          {/* The fleet page itself is switched off above; its editor stays reachable so the
              content is ready whenever it is switched back on. */}
          <Route path="/fleet-admin" element={<FleetAdminPage />} />
          {/* The logos along the foot of the home page. */}
          <Route path="/partners-admin" element={<PartnersAdminPage />} />
          <Route path="/apply" element={<ApplyPage />} />
          <Route
            path="/apply.html"
            element={<Navigate to="/apply" replace />}
          />
          <Route path="/contact" element={<ContactPage />} />
          <Route
            path="/contact.html"
            element={<Navigate to="/contact" replace />}
          />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route
            path="/privacy.html"
            element={<Navigate to="/privacy" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}
